import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';

import { CurationPlatformAppId, getEntitledCurationPlatformApps } from './curationPlatformApps';
import { getCurationPlatformAppUrl } from './curationPlatformUrls';
import type { SidebarAdditionalItem } from '../AppPageLayout/types';

export interface UseCurationPlatformNavItemsOptions {
  /**
   * Which Curation Platform app this hook is called from — a build-time
   * fact passed in by the caller, never derived from the URL (TDD §3.3).
   */
  currentAppId: CurationPlatformAppId;
  /**
   * The current app's own navigation-hub path (e.g.
   * getAnnotationToolDashboardPath()). Resolved by the caller because
   * shared-layout (scope:shared) cannot import either app's route paths:
   * getAnnotationToolDashboardPath lives inside the annotation-tool app
   * itself (libs never import from apps), and getAccuracyDashboardPath
   * lives in a scope:accuracy-dashboard lib, which the enforced dep
   * constraint (eslint.base.config.js) blocks scope:shared from importing.
   * This is unrelated to entitlement — which apps a user may access is
   * already fully decided below via CURATION_PLATFORM_APPS + useAuth().
   */
  ownAppPath: string;
}

export const useCurationPlatformNavItems = ({
  currentAppId,
  ownAppPath,
}: UseCurationPlatformNavItemsOptions): SidebarAdditionalItem[] => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();

  const roleIds = user.roles.map((role) => role.roleId);

  return getEntitledCurationPlatformApps(roleIds).reduce<SidebarAdditionalItem[]>((items, app) => {
    const isCurrentApp = app.id === currentAppId;
    const href = isCurrentApp ? ownAppPath : getCurationPlatformAppUrl(app.id);

    if (href === null) {
      return items;
    }

    items.push({
      position: 1,
      getItem: () => ({
        id: app.id,
        label: intl.formatMessage({ id: app.messageId }),
        icon: app.icon,
        selected: isCurrentApp,
        disabled: false,
        isSelectable: true,
        href,
        onClick: isCurrentApp ? () => navigate(ownAppPath) : () => window.location.assign(href),
        divider: false,
      }),
    });
    return items;
  }, []);
};
